import { useState, useEffect, useRef, useCallback } from 'react';

// Import actual Remotion b-roll components from parent project
import { WinnerCard } from '@broll/WinnerCard';
import { TemplateDashboard } from '@broll/TemplateDashboard';
import { TemplateList } from '@broll/TemplateList';
import { TemplateChat } from '@broll/TemplateChat';
import { TemplateFeature } from '@broll/TemplateFeature';
import type { BrollComponentProps } from '@broll/registry';

// Map component type strings to actual React components
const COMPONENT_MAP: Record<string, React.FC<BrollComponentProps>> = {
  'winner-card': WinnerCard,
  'template-dashboard': TemplateDashboard,
  'template-list': TemplateList,
  'template-chat': TemplateChat,
  'template-feature': TemplateFeature,
};

interface ComponentPreviewProps {
  componentType: string;
  width: number;
  height: number;
  notes?: string;
  componentProps?: Record<string, unknown>;
  animate?: boolean;
  fps?: number;
}

export function ComponentPreview({
  componentType,
  width,
  height,
  notes,
  componentProps,
  animate = true,
  fps = 30,
}: ComponentPreviewProps) {
  const [frame, setFrame] = useState(0);
  const rafRef = useRef<number>(0);
  const startTimeRef = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const replay = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    setFrame(0);
    startTimeRef.current = 0;
    if (animate) {
      rafRef.current = requestAnimationFrame(tick);
    }
  }, [animate]);

  const tick = useCallback((timestamp: number) => {
    if (!startTimeRef.current) startTimeRef.current = timestamp;
    const elapsed = timestamp - startTimeRef.current;
    const newFrame = Math.floor((elapsed / 1000) * fps);
    setFrame(newFrame);
    rafRef.current = requestAnimationFrame(tick);
  }, [fps]);

  // Measure container for scaling
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    updateSize();
    const observer = new ResizeObserver(updateSize);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (animate) {
      rafRef.current = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(rafRef.current);
    }
  }, [animate, tick]);

  const Component = COMPONENT_MAP[componentType];

  if (!Component) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0A1629',
        color: '#64748b',
        fontFamily: 'Poppins, sans-serif',
        fontSize: 14,
        flexDirection: 'column',
        gap: 8,
      }}>
        <span style={{ fontSize: 24 }}>🧩</span>
        <span>{componentType}</span>
      </div>
    );
  }

  // Scale: render at native resolution, then CSS-scale to fit container
  const scale = containerWidth > 0 ? containerWidth / width : 0.2;
  const scaledHeight = height * scale;

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: scaledHeight || '100%',
        position: 'relative',
        overflow: 'hidden',
        background: '#0A1629',
      }}
    >
      {/* Native-resolution component, scaled down */}
      <div style={{
        width: width,
        height: height,
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
      }}>
        <Component
          frame={frame}
          fps={fps}
          width={width}
          height={height}
          durationInFrames={fps * 5}
          notes={notes}
          props={componentProps}
        />
      </div>

      {/* Replay button */}
      <button
        onClick={replay}
        style={{
          position: 'absolute',
          bottom: 6,
          right: 6,
          padding: '4px 10px',
          background: 'rgba(0,0,0,0.7)',
          color: '#fff',
          fontSize: 11,
          fontFamily: 'Poppins, sans-serif',
          borderRadius: 9999,
          border: '1px solid rgba(255,255,255,0.2)',
          cursor: 'pointer',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        ▶ Replay
      </button>
    </div>
  );
}
